class VintageVesselHero extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('[data-vintage-vessel-slide]'));
    this.previousButton = this.querySelector('[data-vintage-vessel-previous]');
    this.nextButton = this.querySelector('[data-vintage-vessel-next]');
    this.currentStatus = this.querySelector('[data-vintage-vessel-current]');
    this.currentIndex = Math.max(
      0,
      this.slides.findIndex((slide) => slide.classList.contains('is-active'))
    );
    this.transitionDuration = Number(this.dataset.transitionDuration || 1500);
    this.autoplayDelay = Number(this.dataset.autoplayDelay || 8000);
    this.autoplayEnabled = this.dataset.autoplay === 'true' && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.autoplayTimer = null;
    this.transitionTimer = null;
    this.isPaused = false;

    if (this.slides.length < 2) {
      this.previousButton?.setAttribute('hidden', '');
      this.nextButton?.setAttribute('hidden', '');
    }

    this.onPreviousClick = () => this.showSlide(this.currentIndex - 1, true);
    this.onNextClick = () => this.showSlide(this.currentIndex + 1, true);
    this.onKeydown = (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.showSlide(this.currentIndex - 1, true);
      }

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.showSlide(this.currentIndex + 1, true);
      }
    };
    this.pauseAutoplay = () => {
      this.isPaused = true;
      this.clearAutoplay();
    };
    this.resumeAutoplay = () => {
      this.isPaused = false;
      this.startAutoplay();
    };
    this.onVisibilityChange = () => {
      if (document.hidden) {
        this.pauseAutoplay();
      } else {
        this.resumeAutoplay();
      }
    };
    this.onBlockSelect = (event) => {
      const selectedSlide = event.target.closest('[data-vintage-vessel-slide]');
      if (!selectedSlide || !this.contains(selectedSlide)) return;

      const selectedIndex = this.slides.indexOf(selectedSlide);
      if (selectedIndex >= 0) this.showSlide(selectedIndex, true);
      this.pauseAutoplay();
    };
    this.onBlockDeselect = (event) => {
      if (this.contains(event.target)) this.resumeAutoplay();
    };

    this.previousButton?.addEventListener('click', this.onPreviousClick);
    this.nextButton?.addEventListener('click', this.onNextClick);
    this.addEventListener('keydown', this.onKeydown);
    this.addEventListener('mouseenter', this.pauseAutoplay);
    this.addEventListener('mouseleave', this.resumeAutoplay);
    this.addEventListener('focusin', this.pauseAutoplay);
    this.addEventListener('focusout', this.resumeAutoplay);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    document.addEventListener('shopify:block:select', this.onBlockSelect);
    document.addEventListener('shopify:block:deselect', this.onBlockDeselect);

    this.updateStatus();
    this.updateInteractiveState();
    this.syncMediaPlayback();
    this.startAutoplay();
  }

  disconnectedCallback() {
    this.previousButton?.removeEventListener('click', this.onPreviousClick);
    this.nextButton?.removeEventListener('click', this.onNextClick);
    this.removeEventListener('keydown', this.onKeydown);
    this.removeEventListener('mouseenter', this.pauseAutoplay);
    this.removeEventListener('mouseleave', this.resumeAutoplay);
    this.removeEventListener('focusin', this.pauseAutoplay);
    this.removeEventListener('focusout', this.resumeAutoplay);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
    document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
    this.clearAutoplay();
    window.clearTimeout(this.transitionTimer);
  }

  showSlide(requestedIndex, userInitiated = false) {
    if (this.slides.length < 2) return;

    const nextIndex = (requestedIndex + this.slides.length) % this.slides.length;
    if (nextIndex === this.currentIndex) return;

    const currentSlide = this.slides[this.currentIndex];
    const nextSlide = this.slides[nextIndex];

    window.clearTimeout(this.transitionTimer);
    this.slides.forEach((slide) => slide.classList.remove('is-leaving'));

    currentSlide.classList.add('is-leaving');
    currentSlide.classList.remove('is-active');
    currentSlide.setAttribute('aria-hidden', 'true');
    nextSlide.classList.add('is-active');
    nextSlide.setAttribute('aria-hidden', 'false');

    this.currentIndex = nextIndex;
    this.updateStatus();
    this.updateInteractiveState();
    this.syncMediaPlayback();

    this.transitionTimer = window.setTimeout(() => {
      currentSlide.classList.remove('is-leaving');
    }, this.transitionDuration);

    if (userInitiated) {
      this.clearAutoplay();
      this.startAutoplay();
    }
  }

  updateStatus() {
    if (!this.currentStatus) return;
    this.currentStatus.textContent = String(this.currentIndex + 1).padStart(2, '0');
  }

  updateInteractiveState() {
    this.slides.forEach((slide, index) => {
      slide.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((element) => {
        if (index === this.currentIndex) {
          element.removeAttribute('tabindex');
        } else {
          element.setAttribute('tabindex', '-1');
        }
      });
    });
  }

  syncMediaPlayback() {
    this.slides.forEach((slide, index) => {
      const video = slide.querySelector('video');
      if (!video) return;

      if (index === this.currentIndex) {
        const playPromise = video.play();
        if (playPromise) playPromise.catch(() => {});
      } else {
        video.pause();
      }
    });
  }

  startAutoplay() {
    if (!this.autoplayEnabled || this.isPaused || this.slides.length < 2) return;
    this.clearAutoplay();
    this.autoplayTimer = window.setTimeout(() => {
      this.showSlide(this.currentIndex + 1);
      this.startAutoplay();
    }, this.autoplayDelay);
  }

  clearAutoplay() {
    window.clearTimeout(this.autoplayTimer);
    this.autoplayTimer = null;
  }
}

if (!customElements.get('vintage-vessel-hero')) {
  customElements.define('vintage-vessel-hero', VintageVesselHero);
}
